import { useEffect, useRef, useState } from "react";
import { trackPurchaseConversion } from "../lib/gtag";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/landing/Logo";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("confirming");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          trackPurchaseConversion(data.amount_total ? data.amount_total / 100 : undefined);
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 20) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0D10] px-5" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-xl border border-white/[0.1] bg-[#12161F] p-10 text-center">
        <div className="flex justify-center"><Logo size={40} /></div>
        {state === "confirming" && (
          <>
            <Loader2 className="mx-auto mt-6 h-8 w-8 animate-spin text-[#F25F22]" />
            <h1 className="mt-5 font-display text-xl font-bold text-slate-50">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-400">One moment while Stripe confirms everything.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mt-6 h-10 w-10 text-emerald-400" />
            <h1 className="mt-5 font-display text-xl font-bold text-slate-50" data-testid="payment-paid-heading">You're in.</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Your account is active. We've emailed you a welcome note — your
              customer portal is ready with the password you chose at signup.
            </p>
            <a
              href="/portal"
              data-testid="payment-portal-button"
              className="mt-6 block rounded-md bg-[#F25F22] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539]"
            >
              Open Your Portal
            </a>
          </>
        )}
        {(state === "timeout" || state === "failed") && (
          <>
            <XCircle className="mx-auto mt-6 h-10 w-10 text-amber-400" />
            <h1 className="mt-5 font-display text-xl font-bold text-slate-50">Still processing</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Your payment is being confirmed. You'll get an email as soon as
              your account activates — try your portal in a few minutes.
            </p>
            <a href="/portal" className="mt-6 block rounded-md border border-white/[0.12] py-3 text-sm font-semibold text-slate-200 hover:border-[#F25F22]/50">
              Go to Portal
            </a>
          </>
        )}
      </div>
    </div>
  );
}
